import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, Plus, Search, Pencil, Copy, Archive, CheckCircle2, MoreHorizontal, CalendarClock, Gem, Send } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { marketingService } from "@/services/marketingService";
import { PLATFORMS, CONTENT_TYPES, CONTENT_STATUSES, platformLabel, contentTypeLabel, statusBadge } from "@/config/marketingOptions";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const ALL = "__all__";

export default function MarketingCenter() {
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState(ALL);
  const [ctype, setCtype] = useState(ALL);
  const [status, setStatus] = useState(ALL);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined, platform: platform === ALL ? undefined : platform, content_type: ctype === ALL ? undefined : ctype, status: status === ALL ? undefined : status, include_archived: status === "archived" };
      setItems((await marketingService.list(params)).content);
    } catch { toast.error("Could not load content"); }
    finally { setLoading(false); }
  }, [search, platform, ctype, status]);

  useEffect(() => { marketingService.brandContext().then(setBrand).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e.message || "Action failed"); } };

  const planned = items.filter((c) => ["planned", "scheduled"].includes(c.status));

  return (
    <AppShell>
      <div className="space-y-6" data-testid="marketing-page">
        <PageHeader eyebrow="AgentHQ · Marketing" title="Marketing Center"
          description="Create, plan and organize your marketing content — grounded in your brand."
          actions={<Button onClick={() => navigate("/marketing/studio")} data-testid="create-content"><Plus className="h-4 w-4" /> Create Content</Button>} />

        {/* Brand + planned summary */}
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2"><CardContent className="flex items-center justify-between p-5">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-foreground"><Gem className="h-4 w-4 text-brand-gold" /> {brand?.business_name || "Your Brand"}</div>
              <p className="mt-1 text-xs text-muted-foreground">
                {brand?.primary_market ? `${brand.primary_market} · ` : ""}{brand?.target_audience || "Add your target audience in Brand Profile"}
                {brand?.brand_voice?.length ? ` · Voice: ${brand.brand_voice.join(", ")}` : ""}
              </p>
              {brand?.default_cta && <p className="mt-1 text-xs text-muted-foreground">Default CTA: “{brand.default_cta}”</p>}
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/brand-profile")} data-testid="edit-brand-profile">Edit Brand Profile</Button>
          </CardContent></Card>
          <Card><CardContent className="flex items-center justify-between p-5">
            <div><p className="text-xs uppercase text-muted-foreground">Planned & Scheduled</p><p className="mt-1 font-heading text-2xl font-bold">{planned.length}</p></div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/content-calendar")} data-testid="open-content-calendar"><CalendarClock className="h-4 w-4" /> Calendar</Button>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex min-w-[180px] flex-1 items-center gap-2 rounded-lg border border-border px-3 sm:max-w-xs">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search content…" className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="content-search" />
          </div>
          <Select value={platform} onValueChange={setPlatform}><SelectTrigger className="w-[150px]" data-testid="filter-platform"><SelectValue placeholder="Platform" /></SelectTrigger><SelectContent><SelectItem value={ALL}>All platforms</SelectItem>{PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
          <Select value={ctype} onValueChange={setCtype}><SelectTrigger className="w-[150px]" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger><SelectContent><SelectItem value={ALL}>All types</SelectItem>{CONTENT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select>
          <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-[140px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value={ALL}>All active</SelectItem>{CONTENT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
        </div>

        {/* Library */}
        {loading ? <div className="py-10"><LoadingState label="Loading content" /></div>
          : items.length === 0 ? (
            <EmptyState title="Your content workspace starts here."
              description="Create your first post, campaign idea, or marketing draft."
              action={<Button className="mt-5" onClick={() => navigate("/marketing/studio")} data-testid="empty-create"><Plus className="h-4 w-4" /> Create Content</Button>} />
          ) : (
            <div className="grid gap-3 md:grid-cols-2" data-testid="content-library">
              {items.map((c) => (
                <Card key={c.id} data-testid={`content-${c.id}`}>
                  <CardContent className="space-y-2 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <button className="text-left" onClick={() => navigate(`/marketing/studio/${c.id}`)}>
                        <p className="font-medium text-foreground hover:text-accent" data-testid={`content-title-${c.id}`}>{c.title}</p>
                        <p className="text-xs text-muted-foreground">{platformLabel(c.platform)} · {contentTypeLabel(c.content_type)}</p>
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="rounded-md border border-border p-1.5 hover:bg-secondary" data-testid={`content-actions-${c.id}`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`/marketing/studio/${c.id}`)} data-testid={`content-edit-${c.id}`}><Pencil className="h-4 w-4" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => act(() => marketingService.duplicate(c.id), "Duplicated")} data-testid={`content-duplicate-${c.id}`}><Copy className="h-4 w-4" /> Duplicate</DropdownMenuItem>
                          {c.status === "draft" && <DropdownMenuItem onClick={() => act(() => marketingService.setStatus(c.id, "planned"), "Planned")} data-testid={`content-plan-${c.id}`}><CalendarClock className="h-4 w-4" /> Mark Planned</DropdownMenuItem>}
                          {c.status === "planned" && <DropdownMenuItem onClick={() => act(() => marketingService.setStatus(c.id, "scheduled"), "Scheduled")}><CalendarClock className="h-4 w-4" /> Mark Scheduled</DropdownMenuItem>}
                          {["planned", "scheduled"].includes(c.status) && <DropdownMenuItem onClick={() => act(() => marketingService.publish(c.id), "Marked published")} data-testid={`content-publish-${c.id}`}><Send className="h-4 w-4" /> Mark as Published (manual)</DropdownMenuItem>}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => act(() => marketingService.archive(c.id), "Archived")} className="text-destructive" data-testid={`content-archive-${c.id}`}><Archive className="h-4 w-4" /> Archive</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {c.body && <p className="line-clamp-2 text-sm text-muted-foreground">{c.body}</p>}
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge className={`capitalize ${statusBadge(c.status)}`}>{c.status}</Badge>
                      {c.manually_published && <span className="text-muted-foreground">Manually marked published</span>}
                      {c.scheduled_for && <span className="text-muted-foreground">Planned for {fmtDate(c.scheduled_for)}</span>}
                      {c.related?.label && <span className="flex items-center gap-1 text-muted-foreground"><CheckCircle2 className="h-3 w-3" /> {c.related.label}</span>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
      </div>
    </AppShell>
  );
}
